import {
  Box,
  Theme,
  SxProps,
  useTheme,
  BoxProps,
  Typography,
  IconButton,
  IconButtonProps,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { red } from "@mui/material/colors";
import {
  TYPOGRAPHY_SCALE,
  SPACING_CONSTANTS,
  COLOR_CONSTANTS,
} from "../../constants/styles";

export type ModalHeaderSize =
  | "small"
  | "medium"
  | "large"
  | "xlarge"
  | "xxlarge";

/**
 * 모달 헤더 props — 제목/부제 + 닫기 버튼 + 옵션 좌측 슬롯(경고 아이콘) + 크기 토큰.
 *
 * `xxlarge` 사용 시 시스템 공지 형태(빨간 배너 + 로고)로 표시된다.
 */
export interface ModalHeaderProps extends BoxProps {
  title: string;
  /**
   * 제목을 이 헤딩 태그로 렌더한다 — 제목이 보조 기술의 헤딩 탐색에 잡히고, 확인창(`role="dialog"`)이
   * `aria-labelledby` 로 가리킬 제목이 된다(`titleId` 와 함께 쓴다). 생략하면 제목은 문단(`<p>`)이다.
   * 태그만 바뀌고 크기별 제목 서체·여백은 그대로라 모양이 같다. 시스템 공지(`xxlarge`)는 `title` 을
   * 그리지 않는 가지라 효과가 없다.
   */
  titleComponent?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /**
   * 제목 요소의 id — 확인창의 `aria-labelledby` 가 이 제목을 접근명으로 가리킬 때 쓴다.
   * 시스템 공지(`xxlarge`)에는 효과가 없다.
   */
  titleId?: string;
  size?: ModalHeaderSize;
  /** 좌측에 경고 아이콘 슬롯 표시 (삭제/위험 확인 모달 용) */
  leftSlot?: boolean;
  subTitle?: string;
  sx?: SxProps<Theme>;
  onClose?: () => void;
  /**
   * 닫기 아이콘 버튼의 접근성 이름 — 아이콘 전용 버튼은 이름이 없으면 보조 기술에 무명 버튼으로
   * 노출된다(a11y 가이드 §2). 호출부가 문구를 넘길 수 있고, 기본값은 현재 로케일의 공용 닫기 문구다.
   */
  closeAriaLabel?: string;
  /**
   * 닫기 버튼에 넘길 속성 — 비활성(`disabled`)·테스트 앵커(`data-testid`) 등. 동작은 `onClose`, 접근명은
   * `closeAriaLabel` 이 정하고 모양은 헤더가 정하므로 그 셋(`onClick`·`aria-label`·`sx`)과 내용은 받지 않는다.
   */
  closeButtonProps?: Omit<
    IconButtonProps,
    "onClick" | "aria-label" | "children" | "sx"
  > & { "data-testid"?: string };
}

export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  (
    {
      sx,
      onClose,
      closeAriaLabel,
      closeButtonProps,
      size = "medium",
      leftSlot = false,
      subTitle,
      title = "Title",
      titleComponent,
      titleId,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const { t } = useTranslation("common");
    const closeLabel = closeAriaLabel ?? t("constants.close");
    // 헤딩을 지정하지 않으면 문단 — Typography 기본 변형(body1)이 그리는 요소(`<p>`)와 같다.
    const titleElement = titleComponent ?? "p";
    // 제목·닫기 버튼(xxlarge 를 뺀 네 크기)과 경고 슬롯(medium·large)은 여기서 한 번만 만든다 —
    // 크기 가지는 이 요소들의 배치만 정한다.
    const titleNode =
      size === "xxlarge" ? null : (
        <Typography component={titleElement} id={titleId} sx={titleText[size]}>
          {title}
        </Typography>
      );
    // medium 만 닫기 아이콘을 Icon 기본값(24)으로 그려 왔다 — 다른 크기(20)에 맞추면 눈에 보이는
    // 디자인 변경이라 지금 렌더를 그대로 고정한다.
    const closeIconSize = size === "medium" ? 24 : 20;
    const closeButton = (
      <IconButton
        {...closeButtonProps}
        sx={iconButton}
        onClick={onClose}
        aria-label={closeLabel}
      >
        <Icon name={CLOSE_ICON_NAME} size={closeIconSize} clickable />
      </IconButton>
    );
    const warningSlot = leftSlot ? (
      <Box sx={subTitle ? leftSlotTop : leftSlotCenter}>
        <Icon
          size={20}
          name="etc/WarningAmber"
          color={theme.palette.warning.main}
        />
      </Box>
    ) : null;
    return (
      <Box ref={ref} sx={{ ...container[size], ...sx }} {...props}>
        {size === "small" && (
          <>
            {titleNode}
            {closeButton}
          </>
        )}
        {size === "medium" && (
          <>
            {warningSlot}
            <Box sx={{ ...textContainer[size] }}>
              {titleNode}
              {subTitle && (
                <Typography sx={subTitleText}>{subTitle}</Typography>
              )}
            </Box>
            {closeButton}
          </>
        )}
        {size === "large" && (
          <>
            {warningSlot}
            <Box sx={{ ...textContainer[size] }}>
              {titleNode}
              {subTitle && (
                <Typography sx={subTitleText}>{subTitle}</Typography>
              )}
            </Box>
            {closeButton}
          </>
        )}
        {size === "xlarge" && (
          <>
            <Box />
            <Box sx={{ ...textContainer[size] }}>
              {titleNode}
              {subTitle && (
                <Typography sx={subTitleText}>{subTitle}</Typography>
              )}
            </Box>
            {closeButton}
          </>
        )}
        {size === "xxlarge" && (
          <>
            <Box sx={logoContainer}>
              <Box component="img" src="/icons/logo/icon.svg" alt="로고" />
              <Box
                component="img"
                src="/icons/logo/name.svg"
                alt="로고"
                width={136}
                sx={logoName}
              />
            </Box>
            <Box
              component="img"
              src="/icons/etc/warning.svg"
              alt="경고"
              sx={warningImage}
            />
            <Typography sx={systemNoticeText}>시스템 점검 공지</Typography>
          </>
        )}
      </Box>
    );
  }
);

ModalHeader.displayName = "ModalHeader";

const baseContainer = {
  width: "100%",
  display: "flex",
} as const;

const mediumLargeContainer = {
  ...baseContainer,
  padding: `${SPACING_CONSTANTS.spacing[4]}px`,
  paddingTop: `${SPACING_CONSTANTS.spacing[6]}px`,
  justifyContent: "space-between",
} as const;

const container = {
  small: {
    ...baseContainer,
    justifyContent: "space-between",
    padding: `${SPACING_CONSTANTS.spacing[4]}px`,
    paddingBottom: `${SPACING_CONSTANTS.spacing[2]}px`,
    borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  medium: mediumLargeContainer,
  large: mediumLargeContainer,
  xlarge: {
    ...baseContainer,
    padding: `${SPACING_CONSTANTS.spacing[10]}px ${SPACING_CONSTANTS.spacing[4]}px ${SPACING_CONSTANTS.spacing[6]}px`,
    alignItems: "center",
    justifyContent: "space-between",
  },
  xxlarge: {
    ...baseContainer,
    height: 243,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    padding: `${SPACING_CONSTANTS.spacing[10]}px ${SPACING_CONSTANTS.spacing[4]}px ${SPACING_CONSTANTS.spacing[6]}px`,
    backgroundColor: red.A700,
  },
} as const;

const baseTextContainer = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: `${SPACING_CONSTANTS.spacing[1]}px`,
} as const;

const mediumLargeTextContainer = {
  ...baseTextContainer,
  flex: 1,
  minWidth: 0,
} as const;

const textContainer = {
  small: baseTextContainer,
  medium: mediumLargeTextContainer,
  large: mediumLargeTextContainer,
  xlarge: {
    ...baseTextContainer,
    width: "100%",
    alignItems: "center",
    gap: `${SPACING_CONSTANTS.spacing[6]}px`,
  },
} as const;

const titleText: Record<Exclude<ModalHeaderSize, "xxlarge">, SxProps<Theme>> = {
  small: TYPOGRAPHY_SCALE.subtitle1,
  medium: TYPOGRAPHY_SCALE.h5,
  large: TYPOGRAPHY_SCALE.h4,
  xlarge: TYPOGRAPHY_SCALE.h4,
};

const CLOSE_ICON_NAME = "20/etc/close";

const iconButton = {
  width: 24,
  height: 24,
  padding: 0,
  flexShrink: 0,
} as const;

const subTitleText = {
  ...TYPOGRAPHY_SCALE.body2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  cursor: "pointer",
  "&:hover": {
    overflow: "auto",
    textOverflow: "unset",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
} as const;

const leftSlotCenter = {
  display: "flex",
  alignItems: "center",
} as const;

const leftSlotTop = {
  alignSelf: "flex-start",
  flexShrink: 0,
} as const;

const logoContainer = {
  display: "flex",
  alignItems: "center",
  gap: 1,
} as const;

const logoName = {
  filter: "drop-shadow(0 0 0 white) brightness(0) invert(1)",
} as const;

const warningImage = {
  width: 72,
  height: 72,
} as const;

const systemNoticeText = {
  ...TYPOGRAPHY_SCALE.h3,
  color: "primary.contrastText",
} as const;
